// Card 66 — Structural governance coherence engine (Phase-4, Tier-6).
//
// Combines Card 61 (governance), Card 62 (governance diff), Card 63
// (governance stability), Card 64 (governance resilience), and Card 65
// (governance immunity) text outputs into a 12-section coherence
// assessment:
//
//   [Coherence Level]             LOW … HIGH
//   [Governance Alignment]        weak / partial / strong
//   [Governance Consistency]      weak / moderate / strong
//   [Cross-Dimension Agreement]   weak / partial / strong
//   [Contradiction Risk]          low / moderate / elevated / high
//   [Coherence Trajectory]        gov → coherence → projected (state)
//   [Coherence Drivers]
//   [Coherence Inhibitors]
//   [Coherence Risks]
//   [Coherence Reinforcement]
//   [Coherence Decay]
//   [System-Level Coherence Summary]
//
// Pure deterministic string IO — no backend, no styling, no new
// dependencies.

const LEVELS = ["LOW", "LOW-MEDIUM", "MEDIUM", "MEDIUM-HIGH", "HIGH"] as const;
type Level = typeof LEVELS[number];

const LEVEL_RANK: Record<Level, number> = {
  LOW: 0, "LOW-MEDIUM": 1, MEDIUM: 2, "MEDIUM-HIGH": 3, HIGH: 4,
};
const RANK_TO_LEVEL: Record<number, Level> = {
  0: "LOW", 1: "LOW-MEDIUM", 2: "MEDIUM", 3: "MEDIUM-HIGH", 4: "HIGH",
};

// ----- Section parser (same pattern as Cards 62-65) -------------------

function parseSection(text: string, header: string): string {
  const re = new RegExp(`\\[${header}\\]\\s*\\n([\\s\\S]*?)(?=\\n\\[|$)`);
  const m  = text.match(re);
  return m ? m[1].trim() : "";
}

function parseLevel(text: string, header: string): Level {
  const body = parseSection(text, header);
  const m = body.match(/^(LOW-MEDIUM|MEDIUM-HIGH|LOW|MEDIUM|HIGH)/);
  return (m ? m[1] : "LOW") as Level;
}

interface Profile {
  invariant:   string;
  threshold:   string;
  upperBranch: string;
  volatility:  string;
}

const DEFAULT_PROFILE: Profile = {
  invariant: "weak", threshold: "weak", upperBranch: "weak", volatility: "weak",
};

function parseProfile(text: string): Profile {
  const body  = parseSection(text, "Governance Profile");
  const lines = body.split("\n").filter((l) => l.startsWith("- "));
  const out: Profile = { ...DEFAULT_PROFILE };
  for (const l of lines) {
    const word = l.match(/^-\s+(\w+)/)?.[1] ?? "weak";
    if      (l.includes("invariant compliance"))      out.invariant   = word;
    else if (l.includes("threshold adherence"))       out.threshold   = word;
    else if (l.includes("upper-branch containment"))  out.upperBranch = word;
    else if (l.includes("volatility control"))        out.volatility  = word;
  }
  return out;
}

function parseListSection(text: string, header: string): string[] {
  const body = parseSection(text, header);
  const out: string[] = [];
  for (const l of body.split("\n")) {
    if (l.startsWith("- ")) out.push(l.slice(2).trim());
  }
  return out;
}

function parseSlope(diff: string): number {
  const body     = parseSection(diff, "Governance Slope").trim();
  const stripped = body.replace(/^\+/, "");
  const n = Number(stripped);
  return Number.isFinite(n) ? n : 0;
}

type Direction = "improving" | "stable" | "deteriorating";

function parseDirection(diff: string): Direction {
  const body = parseSection(diff, "Governance Direction");
  if (body === "improving")     return "improving";
  if (body === "deteriorating") return "deteriorating";
  return "stable";
}

type Pressure = "low" | "moderate" | "high";

function parsePressure(diff: string): Pressure {
  const body = parseSection(diff, "Governance Pressure");
  if (body === "high")     return "high";
  if (body === "moderate") return "moderate";
  return "low";
}

type Card63Coherence = "strong" | "partial" | "weak";

function parseCard63Coherence(stability: string): Card63Coherence {
  const body = parseSection(stability, "Governance Coherence");
  if (body === "strong") return "strong";
  if (body === "weak")   return "weak";
  return "partial";
}

type Integrity = "strong" | "moderate" | "weak";

function parseIntegrity(stability: string): Integrity {
  const body = parseSection(stability, "Governance Integrity");
  if (body === "strong") return "strong";
  if (body === "weak")   return "weak";
  return "moderate";
}

type Vulnerability = "low" | "moderate" | "elevated" | "high";

function parseVulnerability(imm: string): Vulnerability {
  const body = parseSection(imm, "Governance Vulnerability");
  if (body === "high")     return "high";
  if (body === "elevated") return "elevated";
  if (body === "moderate") return "moderate";
  return "low";
}

type Hardening = "weak" | "partial" | "strong";

function parseHardening(imm: string): Hardening {
  const body = parseSection(imm, "Governance Hardening");
  if (body === "strong") return "strong";
  if (body === "weak")   return "weak";
  return "partial";
}

// ----- Strength rank ---------------------------------------------------

function strengthRank(s: string): number {
  switch (s.toLowerCase()) {
    case "weak":     return 0;
    case "partial":  return 1;
    case "moderate": return 1;
    case "full":     return 2;
    case "strong":   return 2;
    default:         return 0;
  }
}

// ----- Derived fields --------------------------------------------------

// Coherence floors on the weakest of the three upstream layers
// (stability / resilience / immunity) — if any one is low, the
// system can't be more coherent than that bottleneck. Penalties
// then apply on top for inhibitor density / threshold breach.
function coherenceLevelOf(
  stability:   Level,
  resilience:  Level,
  immunity:    Level,
  inhibitors:  string[],
  profile:     Profile,
): Level {
  let rank = Math.min(
    LEVEL_RANK[stability], LEVEL_RANK[resilience], LEVEL_RANK[immunity],
  );
  if (inhibitors.length >= 4)              rank -= 1;
  if (strengthRank(profile.threshold) === 0) rank -= 1;
  rank = Math.max(0, Math.min(4, rank));
  return RANK_TO_LEVEL[rank];
}

type Alignment = "weak" | "partial" | "strong";

function alignmentOf(stability: Level, resilience: Level, immunity: Level): Alignment {
  const ranks = [LEVEL_RANK[stability], LEVEL_RANK[resilience], LEVEL_RANK[immunity]];
  const min = Math.min(...ranks);
  const max = Math.max(...ranks);
  const spread = max - min;
  if (spread === 0) {
    if (min >= 3) return "strong";
    if (min === 0) return "weak";
    return "partial";
  }
  if (spread === 1) return "partial";
  return "weak";
}

type Consistency = "weak" | "moderate" | "strong";

function consistencyOf(integrity: Integrity, card63Coherence: Card63Coherence): Consistency {
  if (integrity === "strong" && card63Coherence === "strong") return "strong";
  if (integrity === "weak" || card63Coherence === "weak")     return "weak";
  return "moderate";
}

type Agreement = "weak" | "partial" | "strong";

function agreementOf(
  govLevel: Level, stability: Level, resilience: Level, immunity: Level,
): Agreement {
  const ranks = [
    LEVEL_RANK[govLevel], LEVEL_RANK[stability],
    LEVEL_RANK[resilience], LEVEL_RANK[immunity],
  ];
  const spread = Math.max(...ranks) - Math.min(...ranks);
  if (spread === 0) return "strong";
  if (spread === 1) return "partial";
  return "weak";
}

type ContradictionRisk = "low" | "moderate" | "elevated" | "high";

// Fires high when the layered governance levels are wildly out of
// sync, or vulnerability is high. Tiered downward by inhibitor
// density / vulnerability strength.
function contradictionRiskOf(
  inhibitorCount: number,
  vulnerability:  Vulnerability,
  layerSpread:    number,
): ContradictionRisk {
  if (vulnerability === "high" || layerSpread >= 2)             return "high";
  if (inhibitorCount >= 3 || vulnerability === "elevated")      return "elevated";
  if (inhibitorCount >= 1 || vulnerability === "moderate")      return "moderate";
  return "low";
}

// Trajectory walks gov → coherence → projected; tail mirrors Card 61's
// "(projected)" vs "(stable)" convention.
function buildCoherenceTrajectory(
  govLevel:  Level,
  coherence: Level,
  slope:     number,
): string {
  const cohRank  = LEVEL_RANK[coherence];
  const projRank = Math.max(0, Math.min(4, cohRank + slope));
  const proj     = RANK_TO_LEVEL[projRank];
  const moves    = govLevel !== coherence || coherence !== proj;
  const tail     = moves ? "(projected)" : "(stable)";
  return `${govLevel.toLowerCase()} → ${coherence.toLowerCase()} → ${proj.toLowerCase()} ${tail}`;
}

// ----- Drivers / Inhibitors / Risks / Reinforcement / Decay -----------

function uniqPush(arr: string[], s: string): void {
  if (!arr.includes(s)) arr.push(s);
}

// Coherence layer reports the upstream improvement signals as one
// triple when slope is positive — operators reading the coherence
// pane see the whole stack moving together.
function collectDrivers(slope: number): string[] {
  const out: string[] = [];
  if (slope > 0) {
    uniqPush(out, "improving governance stability");
    uniqPush(out, "improving governance resilience");
    uniqPush(out, "improving immunity trajectory");
  }
  return out;
}

// Card 66 inhibitors pull directly from Card 61's [Governance
// Inhibitors] block (no "persistent" prefix) plus profile-derived
// weaknesses — the coherence pane is a snapshot of the live
// vulnerability surface, not a transition signal.
function collectInhibitors(govInhibitors: string[], profile: Profile): string[] {
  const out: string[] = [];
  for (const i of govInhibitors) uniqPush(out, i);
  if (profile.invariant === "partial") uniqPush(out, "partial invariant compliance");
  if (profile.invariant === "weak")    uniqPush(out, "weak invariant compliance");
  if (profile.threshold === "weak")    uniqPush(out, "weak threshold adherence");
  if (
    profile.upperBranch === "weak" &&
    !govInhibitors.some((i) => i.toLowerCase().includes("upper"))
  ) {
    uniqPush(out, "weak upper-branch containment");
  }
  if (
    profile.volatility === "weak" &&
    !govInhibitors.some((i) => i.toLowerCase().includes("volatility"))
  ) {
    uniqPush(out, "weak volatility control");
  }
  return out;
}

function collectRisks(
  layerSpread:       number,
  profile:           Profile,
  pressure:          Pressure,
  contradictionRisk: ContradictionRisk,
): string[] {
  const out: string[] = [];
  if (layerSpread >= 1)                       uniqPush(out, "cross-dimension divergence");
  if (strengthRank(profile.threshold) < 2)    uniqPush(out, "threshold misalignment");
  if (pressure !== "low" || contradictionRisk !== "low") {
    uniqPush(out, "governance contradiction under pressure");
  }
  return out;
}

function collectReinforcement(profile: Profile, hardening: Hardening): string[] {
  const out: string[] = [];
  if (strengthRank(profile.invariant)   > 0) uniqPush(out, "maintain invariant compliance");
  if (strengthRank(profile.threshold)   > 0) uniqPush(out, "maintain drift suppression");
  if (strengthRank(profile.volatility)  > 0) uniqPush(out, "maintain volatility control");
  if (strengthRank(profile.upperBranch) > 0) uniqPush(out, "maintain upper-branch containment");
  if (hardening !== "weak")                  uniqPush(out, "maintain governance hardening");
  return out;
}

// Card 66 decay order matches the spec demo: drift, volatility,
// thresholds (different from Card 64's threshold-first ordering).
function collectDecay(profile: Profile): string[] {
  const out: string[] = [];
  if (strengthRank(profile.threshold)  < 2) uniqPush(out, "drift may re-emerge");
  if (strengthRank(profile.volatility) < 2) uniqPush(out, "volatility may spike");
  if (strengthRank(profile.threshold)  < 2) uniqPush(out, "thresholds may weaken under load");
  return out;
}

// ----- Summary ---------------------------------------------------------

function shortKey(s: string): string {
  const l = s.toLowerCase();
  if (l.includes("cz") || l.includes("invariant"))   return "CZ";
  if (l.includes("upper"))                            return "upper-branch";
  if (l.includes("drift") || l.includes("threshold")) return "drift";
  if (l.includes("volatility"))                       return "volatility";
  return s;
}

function joinAnd(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function uniqArr(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const i of items) {
    if (!seen.has(i)) { seen.add(i); out.push(i); }
  }
  return out;
}

// "partial" → "moderate" mapping for the summary, matching the spec
// demo where the [Cross-Dimension Agreement] block reads "partial"
// while the trailing sentence says "moderate".
function summaryAgreementWord(a: Agreement): string {
  return a === "partial" ? "moderate" : a;
}

function buildSummary(
  direction:  Direction,
  alignment:  Alignment,
  agreement:  Agreement,
  inhibitors: string[],
): string {
  const inhibitorKeys   = uniqArr(inhibitors.map(shortKey));
  const inhibitorPhrase = joinAnd(inhibitorKeys);

  let s1: string;
  if (direction === "improving") {
    s1 = inhibitors.length > 0
      ? `Governance coherence is improving but remains vulnerable to ${inhibitorPhrase} instability.`
      : `Governance coherence is improving with no remaining inhibitors.`;
  } else if (direction === "deteriorating") {
    s1 = inhibitors.length > 0
      ? `Governance coherence is deteriorating under ${inhibitorPhrase} pressure.`
      : `Governance coherence is deteriorating — reinforcement recommended.`;
  } else {
    s1 = inhibitors.length > 0
      ? `Governance coherence is steady but constrained by ${inhibitorPhrase}.`
      : `Governance coherence is steady with no material changes.`;
  }

  const s2 = `Alignment is ${alignment}, and cross-dimension agreement is ${summaryAgreementWord(agreement)}.`;
  return `${s1} ${s2}`;
}

// ----- Entry point -----------------------------------------------------

export function buildStructuralGovernanceCoherence(
  governance:           string,
  governanceDiff:       string,
  governanceStability:  string,
  governanceResilience: string,
  governanceImmunity:   string,
): string {
  // Parse Card 61 (governance state).
  const govLevel      = parseLevel(governance, "Governance Level");
  const profile       = parseProfile(governance);
  const govInhibitors = parseListSection(governance, "Governance Inhibitors");

  // Parse Card 62 (transition).
  const direction = parseDirection(governanceDiff);
  const slope     = parseSlope(governanceDiff);
  const pressure  = parsePressure(governanceDiff);

  // Parse Card 63 (stability).
  const stability       = parseLevel(governanceStability, "Stability Level");
  const card63Coherence = parseCard63Coherence(governanceStability);
  const integrity       = parseIntegrity(governanceStability);

  // Parse Card 64 (resilience).
  const resilience = parseLevel(governanceResilience, "Resilience Level");

  // Parse Card 65 (immunity).
  const immunity      = parseLevel(governanceImmunity, "Immunity Level");
  const vulnerability = parseVulnerability(governanceImmunity);
  const hardening     = parseHardening(governanceImmunity);

  // Derived fields.
  const inhibitors    = collectInhibitors(govInhibitors, profile);
  const coherence     = coherenceLevelOf(stability, resilience, immunity, inhibitors, profile);
  const alignment     = alignmentOf(stability, resilience, immunity);
  const consistency   = consistencyOf(integrity, card63Coherence);
  const agreement     = agreementOf(govLevel, stability, resilience, immunity);
  const layerSpread   = Math.max(
    LEVEL_RANK[govLevel], LEVEL_RANK[stability], LEVEL_RANK[resilience], LEVEL_RANK[immunity],
  ) - Math.min(
    LEVEL_RANK[govLevel], LEVEL_RANK[stability], LEVEL_RANK[resilience], LEVEL_RANK[immunity],
  );
  const contradiction = contradictionRiskOf(inhibitors.length, vulnerability, layerSpread);
  const trajectory    = buildCoherenceTrajectory(govLevel, coherence, slope);
  const drivers       = collectDrivers(slope);
  const risks         = collectRisks(layerSpread, profile, pressure, contradiction);
  const reinforcement = collectReinforcement(profile, hardening);
  const decay         = collectDecay(profile);
  const summary       = buildSummary(direction, alignment, agreement, inhibitors);

  const blocks: string[] = [];
  blocks.push("=== Structural Governance Coherence ===");
  blocks.push(`[Coherence Level]\n${coherence}`);
  blocks.push(`[Governance Alignment]\n${alignment}`);
  blocks.push(`[Governance Consistency]\n${consistency}`);
  blocks.push(`[Cross-Dimension Agreement]\n${agreement}`);
  blocks.push(`[Contradiction Risk]\n${contradiction}`);
  blocks.push(`[Coherence Trajectory]\n${trajectory}`);
  blocks.push(
    drivers.length === 0
      ? `[Coherence Drivers]\n(none)`
      : `[Coherence Drivers]\n${drivers.map((d) => `- ${d}`).join("\n")}`,
  );
  blocks.push(
    inhibitors.length === 0
      ? `[Coherence Inhibitors]\n(none)`
      : `[Coherence Inhibitors]\n${inhibitors.map((i) => `- ${i}`).join("\n")}`,
  );
  blocks.push(
    risks.length === 0
      ? `[Coherence Risks]\n(none)`
      : `[Coherence Risks]\n${risks.map((r) => `- ${r}`).join("\n")}`,
  );
  blocks.push(
    reinforcement.length === 0
      ? `[Coherence Reinforcement]\n(none)`
      : `[Coherence Reinforcement]\n${reinforcement.map((r) => `- ${r}`).join("\n")}`,
  );
  blocks.push(
    decay.length === 0
      ? `[Coherence Decay]\n(none)`
      : `[Coherence Decay]\n${decay.map((d) => `- ${d}`).join("\n")}`,
  );
  blocks.push(`[System-Level Coherence Summary]\n${summary}`);

  return blocks.join("\n\n");
}
